// The `filterBy` key (and thus the `qq` URL parameter) for the hierarchical
// category filter. It matches the backend query parameter name (`categories[]`),
// which is NOT the same as the filter's own id in the response (`category`).
export const CATEGORY_SELECTION_FILTER_KEY = 'categories';

export const CATEGORY_SELECTION_TYPE = 'categorySelection';

//todo use backend API types after backend API swagger is updated with the correct types
// be documents the `categorySelection` filter's values as FilterValueModel-default (`name`, `value`,
// `iconName`), but GET /api/v2/products actually returns a category tree in
// `filters[].values[].children`. One node of that tree; it also covers the tree
// AwCategorySelectionFilter builds from it, whose synthetic root node has no `value`.
export interface TreeNode {
  name?: string;
  value?: string | number;
  children?: TreeNode[] | null;
  [key: string]: unknown;
}

// Every node, at every level — the filter can be applied to any category, not
// only the leaves.
export function flattenTree (values: TreeNode[]): TreeNode[] {
  const flattened: TreeNode[] = [];
  const collect = (nodes: TreeNode[]) => {
    for (const node of nodes) {
      flattened.push(node);
      if (node.children) {
        collect(node.children);
      }
    }
  };
  collect(values);
  return flattened;
}

// Resolves a `filterBy` key to its filter definition. The hierarchical category
// filter is stored under `categories` but arrives with the id `category`, so that
// one key is matched by selection type instead of by id.
export function findFilterByKey<T extends { id?: string; selectionType?: string }> (
  filters: T[] | undefined,
  filterKey: string,
): T | undefined {
  if (filterKey === CATEGORY_SELECTION_FILTER_KEY) {
    return filters?.find(filter => filter.selectionType === CATEGORY_SELECTION_TYPE);
  }
  return filters?.find(filter => filter.id === filterKey);
}

// Names of the selected categories, in tree order, for the active-filter chip.
export function selectedCategoryNames (
  values: TreeNode[] | undefined,
  selected: unknown,
): string[] {
  const selectedValues = [selected].flat().map(String);
  return flattenTree(values || [])
    .filter(node => selectedValues.includes(String(node.value)))
    .map(node => String(node.name ?? ''));
}

export function findNodeByValue (categoryTree: TreeNode, value: string | number): TreeNode | null {
  const findSubNode = (node: TreeNode): TreeNode | null => {
    if (node.value === value) {
      return node;
    } else if (node.children) {
      for (const child of node.children) {
        const found = findSubNode(child);
        if (found) {
          return found;
        }
      }
    } else {
      return null;
    }
    return null;
  };
  return value === 'root' ? categoryTree : findSubNode(categoryTree);
}

// The ancestors of a node, from its direct parent up to the topmost category.
// The synthetic root the filter builds around the tree has no `value`, so it is
// not an ancestor anyone can select and never ends up in the list.
export function ancestorValues (categoryTree: TreeNode, value: string | number): string[] {
  const ancestors: string[] = [];
  let node = findNodeByValue(categoryTree, value);
  while (node) {
    const parentValue = node.parentValue;
    if (typeof parentValue !== 'string' && typeof parentValue !== 'number') {
      break;
    }
    node = findNodeByValue(categoryTree, parentValue);
    if (!node || node.value === undefined) {
      break;
    }
    ancestors.push(String(node.value));
  }
  return ancestors;
}
